/**
 * The EMR half of a replay, played in step with the panel half.
 *
 * Two recorders produce a session: one in the Comprehend app (the panel) and one
 * in the content script (the EMR's own document, batched into `cpt-emr` sidecar
 * events). They are separate rrweb streams because they are separate documents —
 * there is no way to merge them into one, and rrweb would not know what to do
 * with a second DOM if there were.
 *
 * So this mounts its own Replayer and follows the panel's clock. Both streams
 * carry wall-clock `timestamp`s, which is what makes them alignable at all:
 * panel-relative time is converted to absolute, then to EMR-relative. Idle gaps
 * differ between the two (the EMR is busy while the panel sits still), so
 * seeking on a shared absolute time is the only thing that keeps "the panel
 * showed an error here" pointing at the right frame of the chart.
 *
 * Deliberately has no transport controls: two sets of play/pause that can drift
 * apart is worse than one. The panel's controls drive both.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

/** Flatten the `cpt-emr` batches back into one ordered rrweb stream. */
export function flattenEmrEvents(emrBatches) {
  if (!Array.isArray(emrBatches)) return [];
  const events = [];
  for (const batch of emrBatches) {
    if (batch && Array.isArray(batch.events)) events.push(...batch.events);
  }
  // Batches arrive in drain order, which is already chronological, but a dropped
  // or re-sent batch would break that assumption and rrweb requires monotonic
  // input — it replays events in array order, not by timestamp.
  events.sort((a, b) => (a?.timestamp || 0) - (b?.timestamp || 0));
  events.forEach(dropLinkCrossOrigin);
  return events;
}

/**
 * Let linked stylesheets load in the viewer.
 *
 * Prompt is recorded without inlined CSS (see EmrReplayRecorder.ts), so its
 * replay keeps <link href> tags. Vite marks them `crossorigin`, which makes the
 * browser fetch them with CORS, and Prompt sends no CORS headers — so from the
 * viewer's origin every one is blocked. Without the attribute they load like
 * any cross-origin stylesheet. Edits in place: the attribute means nothing in
 * a reconstruction, and copying snapshots this size would cost more than it
 * protects.
 */
function dropLinkCrossOrigin(event) {
  const strip = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.tagName === 'link' && node.attributes) delete node.attributes.crossorigin;
    if (Array.isArray(node.childNodes)) node.childNodes.forEach(strip);
  };
  if (event?.type === 2) strip(event.data?.node);
  // A route change that loads more CSS adds the <link> as a mutation.
  if (event?.type === 3 && event.data?.source === 0 && Array.isArray(event.data.adds)) {
    event.data.adds.forEach((add) => strip(add?.node));
  }
}

const EmrReplayPane = ({ emrBatches, currentTime, onReplayerReady }) => {
  const hostRef = useRef(null);
  const replayerRef = useRef(null);
  const [status, setStatus] = useState('');

  const events = useMemo(() => flattenEmrEvents(emrBatches), [emrBatches]);
  const origin = events.length ? events[0].timestamp : null;

  useEffect(() => {
    let cancelled = false;
    if (!hostRef.current) return undefined;

    // rrweb needs a full snapshot plus something to apply to it.
    if (events.length < 2) {
      setStatus(
        events.length
          ? 'The EMR recording has only an opening snapshot — nothing to play.'
          : 'No EMR recording in this session (the tab was never on a supported page, or the recorder disabled itself).',
      );
      return undefined;
    }

    setStatus('');
    const host = hostRef.current;

    (async () => {
      // Same dynamic import the viewer uses for the player: rrweb must not land
      // in the main bundle for the majority of loads that never open a replay.
      const { Replayer } = await import('rrweb');
      await import('rrweb/dist/style.css');
      if (cancelled) return;

      host.innerHTML = '';
      replayerRef.current = new Replayer(events, {
        root: host,
        // No autoplay and no mouse tail: this pane is scrubbed, never played on
        // its own, so a tail would just smear across seeks.
        skipInactive: false,
        mouseTail: false,
      });
      // Render the first frame so the pane is not blank before the first seek.
      replayerRef.current.pause(0);
      fitToHost(replayerRef.current, host);
      // Hand the live Replayer up so the injector bridge can run against the DOM
      // it rebuilds. Passing the instance rather than the iframe keeps the
      // bridge honest about what it is talking to: a reconstruction, not a page.
      if (onReplayerReady) onReplayerReady(replayerRef.current);
    })();

    return () => {
      cancelled = true;
      if (onReplayerReady) onReplayerReady(null);
      try {
        replayerRef.current?.pause();
      } catch (e) {
        /* already torn down */
      }
      replayerRef.current = null;
      if (host) host.innerHTML = '';
    };
    // onReplayerReady is intentionally excluded: it is a callback, and
    // re-running this effect would tear down and rebuild the player on every
    // parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Follow the panel's clock.
  useEffect(() => {
    const replayer = replayerRef.current;
    if (!replayer || currentTime == null || origin == null) return;
    const relative = currentTime - origin;
    try {
      // Clamp: before the EMR recording started, hold the first frame rather
      // than throwing; after it ended, hold the last.
      replayer.pause(Math.max(0, relative));
    } catch (e) {
      /* a seek past the end is not worth breaking the pane over */
    }
  }, [currentTime, origin]);

  if (status) {
    return (
      <div className="text-muted" style={{ fontSize: 13, padding: '8px 0' }}>
        {status}
      </div>
    );
  }

  return (
    <div>
      <div className="text-muted" style={{ fontSize: 12, marginBottom: 4 }}>
        EMR page — follows the player above ({events.length} events)
      </div>
      <div
        ref={hostRef}
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: 6,
          height: 420,
        }}
      />
    </div>
  );
};

/**
 * Scale the replayed EMR to the pane.
 *
 * A clinician's EMR is routinely 1920px wide and this pane is not, so without
 * this the replay renders cropped to its top-left corner — which looks like a
 * broken recording rather than a sizing problem.
 */
function fitToHost(replayer, host) {
  try {
    const iframe = replayer.iframe;
    const width = Number(iframe.width) || iframe.offsetWidth;
    const height = Number(iframe.height) || iframe.offsetHeight;
    if (!width || !height) return;
    const scale = Math.min(1, (host.clientWidth - 2) / width, (host.clientHeight - 2) / height);
    replayer.wrapper.style.transform = `scale(${scale})`;
    replayer.wrapper.style.transformOrigin = 'top left';
  } catch (e) {
    /* cosmetic only */
  }
}

export default EmrReplayPane;
